import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Inbox, Wallet, CalendarDays, TrendingUp } from "lucide-react";

const euro = (n) => (Number(n) || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const COLORS = ["#1B2380", "#7c3aed", "#059669", "#d97706", "#db2777", "#0891b2", "#65a30d", "#dc2626"];

function BarList({ title, data, isEuro }) {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => Number(v) || 0));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="font-heading text-base mb-4 text-slate-800">{title}</h3>
      {entries.length === 0 ? <p className="text-sm text-slate-400">Nessun dato.</p> : (
        <div className="space-y-2.5">
          {entries.map(([k, v], i) => (
            <div key={k} data-testid={`bar-${k}`}>
              <div className="flex justify-between text-xs mb-1"><span className="text-slate-600">{k}</span><span className="font-medium text-slate-800">{isEuro ? euro(v) : `${v} gg`}</span></div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(Number(v) / max) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`hr-stat-${label}`}>
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}1A`, color }}><Icon className="h-5 w-5" /></div>
      <div>
        <div className="font-heading text-2xl text-slate-900">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
    {sub && <div className="text-xs text-slate-400 mt-2">{sub}</div>}
  </div>
);

export function HRDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/personale/dashboard").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="hr-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Inbox} color="#d97706" label="Da approvare" value={d.da_approvare.richieste + d.da_approvare.note_spese} sub={`${d.da_approvare.richieste} assenze · ${d.da_approvare.note_spese} note spese`} />
        <StatCard icon={Wallet} color="#1B2380" label="Note da rimborsare" value={d.note_da_rimborsare.count} sub={euro(d.note_da_rimborsare.totale)} />
        <StatCard icon={TrendingUp} color="#059669" label={`Spesa ${d.anno}`} value={euro(d.spesa_totale_anno)} sub="Note approvate" />
        <StatCard icon={CalendarDays} color="#7c3aed" label="Assenze questo mese" value={d.assenze_mese.length} sub="Approvate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BarList title={`Spesa per categoria (${d.anno})`} data={d.spesa_per_categoria} isEuro />
        <BarList title={`Spesa per persona (${d.anno})`} data={d.spesa_per_persona} isEuro />
        <BarList title={`Assenze approvate per tipo (${d.anno})`} data={d.assenze_per_tipo} />
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-heading text-base mb-4 text-slate-800">Assenze del mese in corso</h3>
          {d.assenze_mese.length === 0 ? <p className="text-sm text-slate-400">Nessuna assenza approvata questo mese.</p> : (
            <ul className="space-y-2">
              {d.assenze_mese.map((a, i) => (
                <li key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0" data-testid={`hr-assenza-${i}`}>
                  <span className="font-medium text-slate-700">{a.persona_nome}</span>
                  <span className="text-slate-500">{a.tipo}</span>
                  <span className="text-xs text-slate-400">{a.data_inizio} → {a.data_fine}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
